import api, { type ApiResponse } from "./api";

export type DiscoverAppsFilter = "featured" | "trending" | "new" | "for_sale";

export type DiscoverAppsPageMode = "discover" | "categories";

/** Show grey inventory placeholders while genuine published apps are below this count. */
export const PUBLISHED_INVENTORY_PLACEHOLDER_THRESHOLD = 12;

/** Fixed grey placeholder cards shown while inventory is below the threshold. */
export const MARKETPLACE_INVENTORY_PLACEHOLDER_CARDS = 12;

export function shouldShowMarketplaceInventoryPlaceholders(
  publishedInventoryCount: number | null,
): boolean {
  // null = inventory not resolved yet: keep skeleton cards mounted so the
  // listing area does not collapse/blink before the count arrives.
  return (
    publishedInventoryCount == null ||
    publishedInventoryCount < PUBLISHED_INVENTORY_PLACEHOLDER_THRESHOLD
  );
}

export function shouldFetchDiscoverAppsResults(
  mode: DiscoverAppsPageMode,
  categorySlug: string,
): boolean {
  if (mode === "categories") {
    return categorySlug.length > 0;
  }

  return true;
}

export function getDefaultDiscoverFilter(
  mode: DiscoverAppsPageMode,
  filterFromParams: string | null,
): DiscoverAppsFilter | "" {
  if (filterFromParams) {
    return filterFromParams as DiscoverAppsFilter;
  }

  return mode === "categories" ? "" : "trending";
}


export type DiscoverApp = {
  id: string;
  name: string;
  slug: string;
  problem_solved: string | null;
  short_description: string | null;
  category_name: string | null;
  category_slug: string | null;
  apps_for_sale: boolean;
  published_at: string | null;
  downloads: number;
  reviews: number;
  average_rating: number | null;
  icon_url: string | null;
  is_new?: boolean;
};

export type DiscoverAppsQuery = {
  q?: string;
  category?: string;
  filter?: DiscoverAppsFilter | "";
  letter?: string;
  page?: number;
  limit?: number;
};

const discoverAppsFilters = new Set<DiscoverAppsFilter>([
  "featured",
  "trending",
  "new",
  "for_sale",
]);

export function normalizeDiscoverAppsQuery(
  query: DiscoverAppsQuery,
): DiscoverAppsQuery {
  const normalized: DiscoverAppsQuery = { ...query };

  if (normalized.filter && !discoverAppsFilters.has(normalized.filter)) {
    delete normalized.filter;
  }

  const letter = normalized.letter?.trim();
  if (!letter || !/^[A-Za-z]$/.test(letter)) {
    delete normalized.letter;
  } else {
    normalized.letter = letter.toUpperCase();
  }

  if (normalized.page != null && (!Number.isInteger(normalized.page) || normalized.page < 1)) {
    delete normalized.page;
  }

  if (normalized.limit != null && (!Number.isInteger(normalized.limit) || normalized.limit < 1)) {
    delete normalized.limit;
  }

  return normalized;
}

export function buildDiscoverAppsParams(
  query: DiscoverAppsQuery,
): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  const normalized = normalizeDiscoverAppsQuery(query);

  const trimmedQuery = normalized.q?.trim();
  if (trimmedQuery) {
    params.q = trimmedQuery;
  }

  if (normalized.category) {
    params.category = normalized.category;
  }

  if (normalized.filter) {
    params.filter = normalized.filter;
  }

  if (normalized.letter) {
    params.letter = normalized.letter;
  }

  if (normalized.page != null) {
    params.page = normalized.page;
  }

  if (normalized.limit != null) {
    params.limit = normalized.limit;
  }

  return params;
}

export async function fetchDiscoverApps(
  query: DiscoverAppsQuery = {},
): Promise<DiscoverApp[]> {
  const response = await api.get<ApiResponse<{ apps: DiscoverApp[] }>>(
    "/marketplace/apps",
    {
      params: buildDiscoverAppsParams(query),
    },
  );

  return response.data.data.apps ?? [];
}

const MARKETPLACE_LIST_PAGE_SIZE = 50;

/** Load every page the existing marketplace list endpoint exposes. */
export async function fetchAllDiscoverApps(
  query: DiscoverAppsQuery = {},
): Promise<DiscoverApp[]> {
  const allApps: DiscoverApp[] = [];
  let page = 1;

  while (true) {
    const batch = await fetchDiscoverApps({
      ...query,
      page,
      limit: MARKETPLACE_LIST_PAGE_SIZE,
    });
    allApps.push(...batch);

    if (batch.length < MARKETPLACE_LIST_PAGE_SIZE) {
      break;
    }

    page += 1;
  }

  return allApps;
}

/** Genuine public catalogue size (unfiltered). */
export async function fetchPublishedInventoryCount(): Promise<number> {
  const apps = await fetchAllDiscoverApps();
  return apps.length;
}
